/** Calendar-based scheduling UI.
 *
 * One month-grid engine drives every scheduling surface:
 *   - the visitor picker on the public site (brand styling) - both the
 *     propose-a-time flow and the live free-slot flow
 *   - the admin mini-picker inside an inquiry card (neutral styling)
 *   - the admin appointments calendar
 *
 * The interaction is always the same three quick taps:
 *   month ‹ › → day → time.
 */

import { useEffect, useMemo, useState } from 'react'
import { api, type Locale, type SiteConfig, type Slot } from '../lib/api'
import { INTL_LOCALES, t } from '../lib/i18n'

// ---------------------------------------------------------------------------
// Date helpers (all local time - the workshop and its visitors share a zone)
// ---------------------------------------------------------------------------

export function ymd(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1)
}

function addMonths(date: Date, delta: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + delta, 1)
}

function sameMonth(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()
}

/** Monday-first calendar matrix for the month, padded with nulls. */
function monthMatrix(month: Date): (Date | null)[] {
  const first = startOfMonth(month)
  const lead = (first.getDay() + 6) % 7 // Monday = 0
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate()
  const cells: (Date | null)[] = Array(lead).fill(null)
  for (let day = 1; day <= daysInMonth; day++) {
    cells.push(new Date(month.getFullYear(), month.getMonth(), day))
  }
  while (cells.length % 7) cells.push(null)
  return cells
}

/** Monday-first weekday header labels, straight from Intl per locale.
 *  2024-01-01 was a Monday. */
function weekdayLabels(locale: Locale): string[] {
  return Array.from({ length: 7 }, (_, index) =>
    new Date(2024, 0, 1 + index).toLocaleDateString(INTL_LOCALES[locale], {
      weekday: 'short',
    }),
  )
}

function monthLabel(month: Date, locale: Locale): string {
  return month.toLocaleDateString(INTL_LOCALES[locale], {
    month: 'long',
    year: 'numeric',
  })
}

export function formatDay(date: Date, locale: Locale): string {
  return date.toLocaleDateString(INTL_LOCALES[locale], {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  })
}

export function formatShort(iso: string, locale: Locale): string {
  const date = new Date(iso)
  return (
    date.toLocaleDateString(INTL_LOCALES[locale], {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    }) +
    ', ' +
    date.toLocaleTimeString(INTL_LOCALES[locale], {
      hour: '2-digit',
      minute: '2-digit',
    })
  )
}

// ---------------------------------------------------------------------------
// Month grid
// ---------------------------------------------------------------------------

export interface DayState {
  disabled?: boolean
  /** Small dot under the number - "something here" (free slots / events). */
  marked?: boolean
  /** Stronger badge count for the admin calendar. */
  count?: number
  /** Extra classes for the day cell (e.g. a violet tint for blocked days). */
  tint?: string
  /** Override for the badge colour (defaults to black/white). */
  badgeClass?: string
}

const TONES = {
  public: {
    frame: 'rounded-[2px] border border-line bg-white',
    head: 'font-sans text-[12px] font-semibold uppercase tracking-[0.1em] text-slate',
    nav: 'text-ink hover:bg-paper-deep',
    title: 'font-serif text-[22px] text-ink',
    day: 'font-sans text-[16px] text-ink hover:bg-paper-deep',
    daySelected: 'bg-ink !text-paper hover:bg-ink',
    dayDisabled: 'text-line',
    dot: 'bg-amber',
    dotSelected: 'bg-paper',
    today: 'outline outline-1 outline-line',
  },
  admin: {
    frame: 'border border-neutral-200 bg-white',
    head: 'font-sans text-[10px] uppercase tracking-wide text-neutral-400',
    nav: 'text-neutral-700 hover:bg-neutral-100',
    title: 'font-sans text-sm font-semibold text-neutral-800',
    day: 'font-sans text-[13px] text-neutral-800 hover:bg-neutral-100',
    daySelected: 'bg-neutral-900 !text-white hover:bg-neutral-900',
    dayDisabled: 'text-neutral-300',
    dot: 'bg-neutral-400',
    dotSelected: 'bg-white',
    today: 'outline outline-1 outline-neutral-300',
  },
} as const

export function MonthCalendar({
  locale,
  tone = 'public',
  selected,
  multiSelected,
  onSelect,
  dayState,
  initialMonth,
  onMonthChange,
}: {
  locale: Locale
  tone?: keyof typeof TONES
  selected: string | null
  /** Multi-select mode: every day in this set renders as selected too. */
  multiSelected?: Set<string>
  onSelect: (day: Date) => void
  dayState: (day: Date) => DayState
  initialMonth?: Date
  onMonthChange?: (month: Date) => void
}) {
  const [month, setMonth] = useState(() => startOfMonth(initialMonth ?? new Date()))
  const styles = TONES[tone]
  const today = ymd(new Date())
  const cells = useMemo(() => monthMatrix(month), [month])

  const go = (delta: number) => {
    const next = addMonths(month, delta)
    setMonth(next)
    onMonthChange?.(next)
  }

  // Don't navigate into the past.
  const atCurrentMonth = sameMonth(month, new Date())

  return (
    <div className={`select-none p-4 ${styles.frame}`} data-testid="month-calendar">
      <div className="mb-3 flex items-center justify-between">
        <button
          type="button"
          onClick={() => go(-1)}
          disabled={atCurrentMonth}
          aria-label={t(locale).scheduler.prevMonth}
          data-testid="cal-prev"
          className={`flex h-9 w-9 items-center justify-center transition-colors disabled:opacity-30 ${styles.nav}`}
        >
          ‹
        </button>
        <span className={styles.title} data-testid="cal-month-label">
          {monthLabel(month, locale)}
        </span>
        <button
          type="button"
          onClick={() => go(1)}
          aria-label={t(locale).scheduler.nextMonth}
          data-testid="cal-next"
          className={`flex h-9 w-9 items-center justify-center transition-colors ${styles.nav}`}
        >
          ›
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center">
        {weekdayLabels(locale).map((weekday) => (
          <span key={weekday} className={`pb-1 ${styles.head}`}>
            {weekday}
          </span>
        ))}
        {cells.map((day, index) => {
          if (!day) return <span key={`pad-${index}`} />
          const iso = ymd(day)
          const state = dayState(day)
          const isSelected = selected === iso || (multiSelected?.has(iso) ?? false)
          return (
            <button
              key={iso}
              type="button"
              disabled={state.disabled}
              onClick={() => onSelect(day)}
              aria-pressed={isSelected}
              aria-label={formatDay(day, locale)}
              data-testid="cal-day"
              data-date={iso}
              className={`relative flex aspect-square flex-col items-center justify-center transition-colors disabled:cursor-default disabled:hover:bg-transparent ${styles.day} ${
                state.tint ?? ''
              } ${isSelected ? styles.daySelected : ''} ${state.disabled ? styles.dayDisabled : ''} ${iso === today ? styles.today : ''}`}
            >
              {day.getDate()}
              {state.count ? (
                <span
                  className={`absolute bottom-[3px] rounded-full px-[5px] font-sans text-[9px] leading-[13px] ${
                    isSelected
                      ? 'bg-white text-neutral-900'
                      : (state.badgeClass ?? 'bg-neutral-900 text-white')
                  }`}
                >
                  {state.count}
                </span>
              ) : state.marked && !state.disabled ? (
                <span
                  className={`absolute bottom-[5px] h-[5px] w-[5px] rounded-full ${
                    isSelected ? styles.dotSelected : styles.dot
                  }`}
                />
              ) : null}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Time chips
// ---------------------------------------------------------------------------

export function TimeChips({
  times,
  selected,
  onToggle,
  locale,
  tone = 'public',
  emptyText,
}: {
  times: string[] // ISO datetimes
  selected: Set<string>
  onToggle: (iso: string) => void
  locale: Locale
  tone?: keyof typeof TONES
  emptyText: string
}) {
  if (!times.length) {
    return (
      <p
        className={
          tone === 'public'
            ? 'font-sans text-[17px] text-slate'
            : 'font-sans text-sm text-neutral-500'
        }
        data-testid="no-times"
      >
        {emptyText}
      </p>
    )
  }
  return (
    <div className="grid grid-cols-3 gap-2 sm:grid-cols-4" data-testid="time-grid">
      {times.map((iso) => {
        const active = selected.has(iso)
        const label = new Date(iso).toLocaleTimeString(INTL_LOCALES[locale], {
          hour: '2-digit',
          minute: '2-digit',
        })
        const base =
          tone === 'public'
            ? `rounded-[2px] border py-2.5 font-sans text-[15px] font-semibold transition-colors ${
                active
                  ? 'border-ink bg-ink text-paper'
                  : 'border-line bg-white text-ink hover:bg-paper-deep'
              }`
            : `border py-1.5 font-sans text-[13px] transition-colors ${
                active
                  ? 'border-neutral-900 bg-neutral-900 text-white'
                  : 'border-neutral-300 bg-white text-neutral-800 hover:bg-neutral-100'
              }`
        return (
          <button
            key={iso}
            type="button"
            onClick={() => onToggle(iso)}
            aria-pressed={active}
            data-testid="time-option"
            className={base}
          >
            {label}
          </button>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Opening-hours maths (mirrors the backend's candidate_slots)
// ---------------------------------------------------------------------------

type Hours = Record<string, [string, string][]>

function earliestBookable(config: Pick<SiteConfig, 'min_notice_hours'>): Date {
  return new Date(Date.now() + (config.min_notice_hours || 0) * 3600 * 1000)
}

function latestBookable(config: Pick<SiteConfig, 'max_advance_days'>): Date {
  const limit = new Date()
  limit.setDate(limit.getDate() + (config.max_advance_days || 60))
  return limit
}

export function dayIsOpen(day: Date, hours: Hours): boolean {
  const windows = hours[String((day.getDay() + 6) % 7)] ?? []
  return windows.length > 0
}

/** All proposable times for a day, from the published opening hours. */
export function timesForDay(day: Date, config: SiteConfig): string[] {
  const hours = (config.opening_hours ?? {}) as Hours
  const windows = hours[String((day.getDay() + 6) % 7)] ?? []
  const step = (config.slot_minutes || 60) + (config.slot_buffer_minutes || 0)
  const length = config.slot_minutes || 60
  const earliest = earliestBookable(config)
  const out: string[] = []
  for (const window of windows) {
    if (!window || window.length !== 2) continue
    const [fromH, fromM] = window[0].split(':').map(Number)
    const [toH, toM] = window[1].split(':').map(Number)
    const cursor = new Date(day)
    cursor.setHours(fromH, fromM || 0, 0, 0)
    const end = new Date(day)
    end.setHours(toH, toM || 0, 0, 0)
    while (cursor.getTime() + length * 60_000 <= end.getTime()) {
      if (cursor >= earliest) out.push(cursor.toISOString())
      cursor.setMinutes(cursor.getMinutes() + step)
    }
  }
  return out
}

// ---------------------------------------------------------------------------
// The visitor scheduler
// ---------------------------------------------------------------------------

interface VisitorProps {
  config: SiteConfig
  locale: Locale
  /** 'request' → visitor proposes times; 'live' → verified free slots only. */
  mode: 'request' | 'live'
  /** request mode: all chosen proposals (ISO). */
  proposed: string[]
  onProposed: (times: string[]) => void
  /** live mode: the single chosen slot. */
  slot: string | null
  onSlot: (iso: string | null) => void
  /** live mode fallback notice + switch handled by the parent. */
  onCalendarUnavailable?: () => void
}

export function VisitorScheduler({
  config,
  locale,
  mode,
  proposed,
  onProposed,
  slot,
  onSlot,
  onCalendarUnavailable,
}: VisitorProps) {
  const strings = t(locale)
  const [selectedDay, setSelectedDay] = useState<string | null>(null)
  const [freeByDay, setFreeByDay] = useState<Map<string, string[]> | null>(null)
  const [blockedDays, setBlockedDays] = useState<Set<string>>(new Set())
  const [loadingSlots, setLoadingSlots] = useState(mode === 'live')

  const latest = latestBookable(config)
  const earliest = earliestBookable(config)
  const maxSlots = config.max_proposed_slots || 3

  // Days the owner marked as away are greyed out in every mode. (In live mode
  // they're already absent from availability; this covers request mode too.)
  useEffect(() => {
    api
      .get<{ days: string[] }>(`/api/blocked-days?from=${ymd(new Date())}&to=${ymd(latest)}`)
      .then((data) => setBlockedDays(new Set(data.days)))
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Live mode: load the availability for the whole bookable window once.
  useEffect(() => {
    if (mode !== 'live') return
    let cancelled = false
    setLoadingSlots(true)
    const from = ymd(new Date())
    const to = ymd(latest)
    api
      .get<{ slots: Slot[]; warning: string | null }>(`/api/availability?from=${from}&to=${to}`)
      .then((data) => {
        if (cancelled) return
        if (data.warning || data.slots.length === 0) {
          onCalendarUnavailable?.()
          return
        }
        const grouped = new Map<string, string[]>()
        for (const free of data.slots) {
          const key = ymd(new Date(free.start))
          grouped.set(key, [...(grouped.get(key) ?? []), free.start])
        }
        setFreeByDay(grouped)
      })
      .catch(() => onCalendarUnavailable?.())
      .finally(() => !cancelled && setLoadingSlots(false))
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode])

  const dayState = (day: Date): DayState => {
    const endOfDay = new Date(day)
    endOfDay.setHours(23, 59, 59)
    if (endOfDay < earliest || day > latest) return { disabled: true }
    if (blockedDays.has(ymd(day))) return { disabled: true }
    if (mode === 'live') {
      const free = freeByDay?.get(ymd(day))
      return { disabled: !free?.length, marked: !!free?.length }
    }
    // Ask the very function that fills the time grid. "Is the workshop open
    // that day?" is not the same question: on the notice-period boundary a day
    // can be open and still have every one of its slots fall inside the notice
    // window, which offered a day that then showed no times at all.
    return { disabled: timesForDay(day, config).length === 0, marked: false }
  }

  const timesForSelected = useMemo(() => {
    if (!selectedDay) return []
    const day = new Date(`${selectedDay}T00:00:00`)
    if (mode === 'live') return freeByDay?.get(selectedDay) ?? []
    return timesForDay(day, config)
  }, [selectedDay, mode, freeByDay, config])

  const selectedSet = useMemo(
    () => new Set(mode === 'live' ? (slot ? [slot] : []) : proposed),
    [mode, slot, proposed],
  )

  const toggleTime = (iso: string) => {
    if (mode === 'live') {
      onSlot(slot === iso ? null : iso)
      return
    }
    if (proposed.includes(iso)) {
      onProposed(proposed.filter((p) => p !== iso))
    } else if (proposed.length < maxSlots) {
      onProposed([...proposed, iso].sort())
    }
  }

  return (
    <div data-testid="scheduler" data-mode={mode}>
      <div className="grid gap-5 md:grid-cols-[minmax(0,340px)_1fr]">
        <MonthCalendar
          locale={locale}
          tone="public"
          selected={selectedDay}
          onSelect={(day) => setSelectedDay(ymd(day))}
          dayState={dayState}
        />

        <div className="min-h-[200px]">
          {loadingSlots ? (
            <p className="font-sans text-[17px] text-slate">{strings.form.loadingSlots}</p>
          ) : selectedDay ? (
            <>
              <p className="mb-3 font-serif text-[22px] text-ink" data-testid="selected-day-label">
                {formatDay(new Date(`${selectedDay}T00:00:00`), locale)}
              </p>
              <TimeChips
                times={timesForSelected}
                selected={selectedSet}
                onToggle={toggleTime}
                locale={locale}
                tone="public"
                emptyText={strings.form.noSlots}
              />
              {mode === 'request' && proposed.length >= maxSlots ? (
                <p className="mt-3 font-sans text-[14px] text-slate">
                  {strings.form.maxReached}
                </p>
              ) : null}
            </>
          ) : (
            <p className="font-sans text-[17px] leading-[1.6] text-slate">
              {strings.form.pickDayFirst}
            </p>
          )}
        </div>
      </div>

      {/* Chosen times as removable pills. */}
      {mode === 'request' && proposed.length > 0 ? (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="selected-slots">
          {proposed.map((iso) => (
            <button
              key={iso}
              type="button"
              onClick={() => onProposed(proposed.filter((p) => p !== iso))}
              title={strings.form.removeTime}
              data-testid="selected-slot-pill"
              className="group flex items-center gap-2 rounded-[2px] border border-ink bg-ink px-3 py-1.5 font-sans text-[14px] font-semibold text-paper transition-colors hover:bg-white hover:text-ink"
            >
              {formatShort(iso, locale)}
              <span aria-hidden="true" className="text-[15px] leading-none">
                ×
              </span>
            </button>
          ))}
        </div>
      ) : null}
      {mode === 'live' && slot ? (
        <p className="mt-4 rounded-[2px] border border-line bg-paper-deep px-4 py-3 font-sans text-[17px] text-ink" data-testid="chosen-slot">
          {formatShort(slot, locale)}
        </p>
      ) : null}
    </div>
  )
}
